<template>
  <div class="encryption-field">
    <div style="display: flex; align-items: center;">
      <slot :value="isEncrypted && !isDecrypted ? '' : displayValue">
        <span style="font-weight: bold">{{ displayValue }}</span>
      </slot>
      <sensitive-view-button
        v-if="isEncrypted" :revealed="isDecrypted" :disabled="!canView"
        :loading="loading" :label="hint" @click="toggleDecrypt"
      />
    </div>
    <span v-if="errorMessage" class="encryption-error" role="alert">{{ errorMessage }}</span>
  </div>
</template>

<script>
import { sensitiveViewPlaintext } from '@/api/user';
import { isSensitiveCiphertext } from '@/utils/sensitiveData';
import SensitiveViewButton from '@/components/SensitiveViewButton/index.vue';

export default {
  name: 'EncryptionField',
  components: { SensitiveViewButton },
  props: {
    fieldName: { type: String, required: true },
    encryptedValue: { type: String, default: '' },
    label: { type: String, required: true }
  },
  data() {
    return { decryptedValue: '', isDecrypted: false, loading: false, errorMessage: '', requestVersion: 0 };
  },
  computed: {
    isEncrypted() { return isSensitiveCiphertext(this.encryptedValue); },
    identity() { return `${this.$store.getters.id || ''}:${this.$store.getters.token || ''}`; },
    canView() { return (this.$store.getters.button_permissions || []).includes('field:customer-sensitive-info'); },
    hint() {
      return this.$t(!this.canView ? 'sensitiveView.forbidden' : (this.isDecrypted ? 'sensitiveView.hide' : 'sensitiveView.show'));
    },
    displayValue() {
      if (this.isDecrypted) return this.decryptedValue;
      if (!this.encryptedValue) return '--';
      return this.isEncrypted || this.encryptedValue.trim().startsWith('{$') ? '********' : this.encryptedValue;
    }
  },
  watch: {
    encryptedValue: 'reset', fieldName: 'reset', identity: 'reset', canView: 'reset'
  },
  beforeDestroy() { this.reset(); },
  deactivated() { this.reset(); },
  methods: {
    reset() {
      this.requestVersion++;
      this.decryptedValue = '';
      this.isDecrypted = false;
      this.loading = false;
      this.errorMessage = '';
    },
    async toggleDecrypt() {
      if (this.loading || !this.canView || !this.isEncrypted) return;
      if (this.isDecrypted) {
        this.reset();
        this.$emit('hidden', { fieldName: this.fieldName });
        return;
      }
      const version = ++this.requestVersion;
      const identity = this.identity;
      const field = this.fieldName;
      const ciphertext = this.encryptedValue;
      this.loading = true;
      this.errorMessage = '';
      try {
        const response = await sensitiveViewPlaintext({ data: { [field]: ciphertext }});
        if (version !== this.requestVersion || identity !== this.identity || ciphertext !== this.encryptedValue || !this.canView) return;
        const value = response && response.data && response.data[field];
        if (typeof value !== 'string' || isSensitiveCiphertext(value)) throw new Error('Invalid sensitive response');
        this.decryptedValue = value;
        this.isDecrypted = true;
        this.$emit('decrypted', { fieldName: field, value });
      } catch (error) {
        if (version === this.requestVersion) this.errorMessage = error.msg || this.$t('sensitiveView.failed');
      } finally {
        if (version === this.requestVersion) this.loading = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
.encryption-field {
  .encryption-error { display: block; color: #d93025; font-size: 12px; overflow-wrap: anywhere; }
}
</style>
